// Login route — '/login'.
//
// Flow:
//   1. react-hook-form + zod validate { email, password } client-side.
//   2. On submit we POST `action=login` to the PHP `/api/auth/` endpoint
//      as FormData (per the PHP convention: $_POST takes precedence over
//      JSON, and the admin panel has always posted form-data).
//   3. `skipAuthRedirect: true` tells api-client NOT to trigger its
//      global 401 handler — a failed login renders an inline alert above
//      the form instead of bouncing back to this same screen.
//   4. On success we cache the access token + user in the persisted
//      auth store, invalidate any stale ['account'] query data left over
//      from a previous session, and navigate to /admin.
//
// SECURITY: the password field uses autoComplete="current-password" so
// the browser/password manager can fill it correctly, and we never log
// or echo the password value anywhere — the alert surfaces only the
// API's error message (already scrubbed by the PHP layer).
import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { apiClient, ApiError, toFormDataPhp } from '@/lib/api-client'
import { resolveAccessExpiry, useAuthStore } from '@/stores/auth-store'
import type { AuthUser, LoginResponse } from '@/lib/types'

const loginSchema = z.object({
  email: z.string().email('Enter a valid email address.'),
  password: z.string().min(1, 'Password is required.'),
})

type LoginFormValues = z.infer<typeof loginSchema>

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

function LoginPage() {
  useDocumentTitle('Sign in')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = async (values: LoginFormValues): Promise<void> => {
    setSubmitting(true)
    setError(null)
    try {
      // PHP /api/auth/ expects action=login + email + password as form-data.
      const body = toFormDataPhp({
        action: 'login',
        email: values.email,
        password: values.password,
      })
      const res = await apiClient.post<LoginResponse>('/auth/', body, {
        skipAuthRedirect: true,
      })

      if (
        !res ||
        res.success !== true ||
        !res.access_token ||
        !res.refresh_token
      ) {
        throw new ApiError('Login failed.', 401)
      }

      // PHP /api/auth/ login echoes the user object alongside tokens
      // (JWTAuth::login). Fall back to a minimal best-effort identity
      // only when an older backend hasn't been redeployed yet —
      // role='admin' is the safe under-privileged default.
      const user: AuthUser = res.user ?? {
        id: 'me',
        email: values.email,
        role: 'admin',
      }

      useAuthStore.getState().setSession(
        res.access_token,
        user,
        res.refresh_token,
        resolveAccessExpiry(res.expires_in, res.expires_at),
      )
      // Drop any account cache from a previous identity so the next
      // /account render fetches fresh.
      await queryClient.invalidateQueries({ queryKey: ['account'] })
      toast.success('Signed in.')
      await navigate({ to: '/admin', replace: true })
    } catch (err) {
      const message =
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Login failed.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="access-art flex min-h-[calc(100dvh-3.5rem)] w-full flex-col items-center justify-center px-4 py-12">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1 pb-2">
          <h1 className="font-display text-4xl leading-tight tracking-wide">Sign in</h1>
          <p className="text-sm text-muted-foreground">
            Use your LiveVane admin account.
          </p>
        </CardHeader>
        <CardContent>
          {error ? (
            <div
              role="alert"
              data-testid="login-error"
              className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-foreground"
            >
              {error}
            </div>
          ) : null}
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="username"
                        inputMode="email"
                        placeholder="name@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          className="pr-16"
                          {...field}
                        />
                      </FormControl>
                      <button
                        type="button"
                        aria-pressed={showPassword}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 my-1 mr-1 rounded-md px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid="login-toggle-password"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={submitting}
                data-testid="login-submit"
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </Form>
          <p className="mt-5 text-sm text-muted-foreground">
            Forgot your password? Ask an administrator to reset it.
          </p>
        </CardContent>
      </Card>
      <footer className="mt-8 w-full max-w-sm text-center text-sm text-muted-foreground">
        Phazer Rules 2026
      </footer>
    </main>
  )
}
